import React, { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Reports } from '../../../api/relevant';
import useReports from '../../../hooks/useBreakpoint/useReports';
import Base from '../../../layouts/Base';
import { ConfirmDialog } from '../../ConfirmDialog';
import { useToast } from '../../../hooks/useToast';

function useDeleteReport({
	reportId,
	report,
}: {
	reportId: string;
	report?: {
		subscriptions: any[];
		userId: string;
		isPublic: boolean;
	}
}) {
	const location = useLocation();
	const history = useHistory();
	const { reports, updateReports } = useReports();
	const [, showToast] = useToast();

	return useCallback(async () => {
		const ok = await Base.renderGlobal((closeFn) => {
			// Extra confirmation if report is shared or has subscriptions
			const isPublic = report?.isPublic;
			const subs = report?.subscriptions.length;
			const hasSubscriptions = subs > 0;
			if (isPublic || hasSubscriptions) {
				return (
					<ConfirmDialog
						open
						text={(
							<>
								<span className="whitespace-pre-wrap">
									This report
									{isPublic && <> is <span className="font-bold">publicly shared</span></>}
									{isPublic && hasSubscriptions && ' and'}
									{hasSubscriptions && <> has <span className="font-bold">{subs} subscription{subs > 1 && 's'}</span></>}
									. Are you sure you want to delete this report?
								</span>
								<span>Others will no longer be able to view this report, and subscribers will no longer receive updates.</span>
							</>
						)}
						confirmLabel="Delete"
						dangerous
						onAny={closeFn}
						className="max-w-[500px]"
					/>
				);
			} else {
				return (
					<ConfirmDialog
						open
						text="Are you sure you want to delete this report?"
						confirmLabel="Delete"
						dangerous
						onAny={closeFn}
					/>
				);
			}
		});
		if (!ok) {
			return;
		}

		try {
			await Reports.call('deleteReport', { id: reportId });
			updateReports();
			showToast({ message: 'Report deleted', status: 'success' });
			history.push('/reports');
		} catch (err) {
			console.error(err);
			showToast({ message: 'Failed to delete report. Please try again.', status: 'error' });
		}
	}, [history, location.pathname, reportId, reports, updateReports]);
}

export default useDeleteReport;
